import React, { useMemo } from 'react';

import dayjs from 'dayjs';

import { createLocale } from 'locales';

import AvailabilityCalendar from 'components-new/availability-calendar/availability-calendar';

import Typography from 'components-new/typography/typography';

import type { OrganisationPeriodJob } from 'state/__generated__/tadaahAPI';

import useGetFreelancerAvailability from 'state/remote/remote-organization/use-get-freelancer-availability';

import useFreelancerDetails from 'state/remote/remote-organization/use-freelancer-details';

import { getRepeatedDates } from 'utils/date';

interface FreelancerAvailabilityProps {
  periodJob: OrganisationPeriodJob;
  freelancerId: string;
}

const localize = createLocale('organisationPeriodJobsLocales');

const FreelancerAvailability: React.FC<FreelancerAvailabilityProps> = (
  props,
) => {
  const { periodJob, freelancerId } = props;
  const { data: availability, isLoading: loadingAvailability } =
    useGetFreelancerAvailability(
      parseInt(freelancerId, 10),
      periodJob.from_date,
      periodJob.to_date,
      periodJob.start_time,
      periodJob.end_time,
    );

  const { data: freelancerDetails } = useFreelancerDetails(
    parseInt(freelancerId || '0', 10),
    periodJob.id,
  );
  const repeatedDates = getRepeatedDates(
    dayjs(periodJob?.from_date, 'YYYY-MM-DD'),
    dayjs(periodJob?.to_date, 'YYYY-MM-DD'),
    periodJob?.weekdays_in?.split(',')?.map((item) => parseInt(item, 10)) || [],
  );

  const availabledays = useMemo(
    () =>
      availability.filter((val) => {
        return (
          val.available &&
          !!repeatedDates.find((date) =>
            date.isSame(dayjs(val.date, 'YYYY-MM-DD')),
          )
        );
      }),
    [availability, repeatedDates],
  );

  if (!periodJob || !freelancerDetails || !availability || loadingAvailability)
    return <></>;
  return (
    <div className="flex flex-col items-center justify-start h-[400px] w-full sm:w-1/2 lg:w-1/3 mx-4 ml-0 sm:ml-4 sm:mx-auto mt-4 sm:mt-0 xl:w-1/4 border border-solid  border-gray-100 shadow-6 rounded-md">
      <Typography as="h3" variant="body-large-bold" className="my-3">
        {localize('availability')}
      </Typography>
      <AvailabilityCalendar
        fromDate={dayjs(periodJob?.from_date, 'YYYY-MM-DD')}
        toDate={dayjs(periodJob?.to_date, 'YYYY-MM-DD')}
        weekdays={
          periodJob?.weekdays_in
            ?.split(',')
            ?.map((item) => parseInt(item, 10)) || []
        }
        AvailabilityList={availability}
      />
      <Typography variant="body-large" as="p" className="mt-2 w-[236px]">
        {localize('freelancerAvailabilityText')}
        <b>
          {localize('freelancerAvailabilityStats', {
            available: availabledays.length.toString(),
            perc: ((availabledays.length / repeatedDates.length) * 100)
              .toFixed(2)
              .toString(),
            total: repeatedDates.length.toString(),
          })}
        </b>
      </Typography>
      <div className="flex mt-4 mb-3 w-[236px] items-center justify-between">
        <div className="flex justify-between items-center">
          <div className="w-[12px] h-[12px] bg-destructive-100 rounded-sm mr-1" />
          <p className="text-[9px]">{localize('notAvailable')}</p>
        </div>
        <div className="flex justify-between items-center">
          <div className="w-[12px] h-[12px] bg-[#F7F3DA] rounded-sm mr-1" />
          <p className="text-[9px]">{localize('notFilledIn')}</p>
        </div>
        <div className="flex justify-between items-center">
          <div className="w-[12px] h-[12px] bg-[#DAF7F5] rounded-sm mr-1" />
          <p className="text-[9px]">{localize('available')}</p>
        </div>
      </div>
    </div>
  );
};

export default FreelancerAvailability;
